import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { adminUsersAPI } from '../services/adminApi'

interface AdminUser {
  adminId: string
  email: string
  role: string
  status: string
  enabled: boolean
  createdAt: string
  lastModified?: string
}

interface AdminUsersState {
  users: AdminUser[]
  loading: boolean
  error: string | null
  lastFetched: number | null
}

const initialState: AdminUsersState = {
  users: [],
  loading: false,
  error: null,
  lastFetched: null
}

// Async thunks
export const fetchAdminUsers = createAsyncThunk(
  'adminUsers/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminUsersAPI.listUsers()
      return response.users
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch admin users')
    }
  }
)

export const createAdminUser = createAsyncThunk(
  'adminUsers/create',
  async (
    { email, temporaryPassword, role }: { email: string; temporaryPassword: string; role?: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await adminUsersAPI.createUser(email, temporaryPassword, role)
      // Refresh the list after creating
      dispatch(fetchAdminUsers())
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create admin user')
    }
  }
)

export const deleteAdminUser = createAsyncThunk(
  'adminUsers/delete',
  async (email: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await adminUsersAPI.deleteUser(email)
      // Refresh the list after deleting
      dispatch(fetchAdminUsers())
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete admin user')
    }
  }
)

export const updateAdminUser = createAsyncThunk(
  'adminUsers/update',
  async ({ email, role }: { email: string; role: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await adminUsersAPI.updateUser(email, role)
      // Refresh the list after updating
      dispatch(fetchAdminUsers())
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update admin user')
    }
  }
)

const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    clearAdminUsersError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    // Fetch admin users
    builder
      .addCase(fetchAdminUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload
        state.lastFetched = Date.now()
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Create admin user
    builder
      .addCase(createAdminUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createAdminUser.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(createAdminUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Delete admin user
    builder
      .addCase(deleteAdminUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteAdminUser.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(deleteAdminUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update admin user
    builder
      .addCase(updateAdminUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateAdminUser.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  }
})

export const { clearAdminUsersError } = adminUsersSlice.actions
export default adminUsersSlice.reducer
